import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { sanitizeQueryParam } from '@v2/lib';
import toast from 'react-hot-toast';
import {
  BitwerxOfferingsTable,
  BitwerxOfferingPluginManagementPanel,
  BitwerxOfferingSubOfferingPanel,
} from './components';
import {
  BitwerxOffering,
  BitwerxSubOfferingDetail,
  OfferingPlugin,
  UpdateBitwerxOfferingPayload,
} from '@v2/types';
import { usePermissionsContext, Permissions } from '@v2/contexts';
import {
  useBitwerxOfferings,
  useBitwerxOfferingDetails,
  useBitwerxOfferingPlugins,
  useRemovePluginFromOffering,
  useRemoveBitwerxSubOffering,
  useRemoveBitwerxOffering,
  useUpdateBitwerxOffering,
  useTableFilters,
} from '@v2/hooks';
import {
  Button,
  Input,
  Modal,
  CollapsiblePanel,
  DetailField,
  detailFieldStyles,
} from '@v2/components';
import type { ActionMenuItem } from '@v2/components';
import { SettingsPageLayout, DetailPanel, ListDetailView } from '@v2/layouts';
import { DeleteConfirmationModal } from '@v2/components';
import { ManageOfferingPlugins, ManageOfferingsForm } from '@v2/features';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { styles } from './styles';

const BitwerxOfferingsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const selectedOfferingId = sanitizeQueryParam(searchParams.get('offeringId'));

  const { hasPermission } = usePermissionsContext();
  const canSeePluginManagement = hasPermission([Permissions.BitwerxOfferingPluginManagerView]);
  const canAddOrRemovePlugin = hasPermission([Permissions.BitwerxOfferingPluginManagerEdit]);
  const canModifyOfferings = hasPermission(Permissions.ModifyBitwerxOfferings);

  const {
    offerings,
    loading: offeringsLoading,
    error: offeringsError,
    refetch: refetchOfferings,
  } = useBitwerxOfferings();

  const { filteredData, search, setSearch, sortConfig, handleSort } = useTableFilters({
    data: offerings,
    searchKeys: ['name'],
    initialSort: { key: 'name', direction: 'asc' },
  });

  const {
    offering,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = useBitwerxOfferingDetails(selectedOfferingId);

  const {
    plugins,
    loading: pluginsLoading,
    error: pluginsError,
    refetch: refetchPlugins,
  } = useBitwerxOfferingPlugins(offering?.name ?? null);

  const { removePluginFromOffering } = useRemovePluginFromOffering();
  const { removeBitwerxSubOffering } = useRemoveBitwerxSubOffering();
  const { deleteBitwerxOffering, loading: deleteOfferingLoading } = useRemoveBitwerxOffering();
  const { updateBitwerxOffering, loading: updateOfferingLoading } = useUpdateBitwerxOffering();

  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);

  const handleStartEditName = () => {
    setEditName(offering?.name ?? '');
    setIsEditNameOpen(true);
  };

  const handleSaveEditName = async () => {
    if (!offering || !editName.trim() || editName.trim() === offering.name) {
      setIsEditNameOpen(false);
      setEditName('');
      return;
    }
    const trimmedName = editName.trim();
    const payload: UpdateBitwerxOfferingPayload = {
      id: offering.id,
      displayId: 0,
      name: trimmedName,
      newName: trimmedName,
      subOfferings: (offering.subOfferings ?? []).map(sub => ({
        name: sub.name,
        displayName: sub.displayName ?? sub.name,
        addition: false,
      })),
      createdById: offering.createdById,
      createdDate: offering.createdDate,
      createdByFirstName: offering.createdByFirstName,
      createdByLastName: offering.createdByLastName,
      modifiedById: offering.modifiedById,
      modifiedDate: offering.modifiedDate,
    };
    const result = await updateBitwerxOffering(offering.id, payload);
    if (result !== null) {
      toast.success('Offering name updated');
      setIsEditNameOpen(false);
      setEditName('');
      refetchDetails();
      refetchOfferings();
    } else {
      toast.error('Failed to update offering name');
    }
  };

  const handleConfirmDeleteOffering = async () => {
    if (!offering) return;
    const result = await deleteBitwerxOffering(offering.id);
    if (result !== null) {
      toast.success('Offering deleted');
      setIsConfirmDeleteOpen(false);
      refetchOfferings();
      searchParams.delete('offeringId');
      history.push({ search: searchParams.toString() });
    } else {
      toast.error('Failed to delete offering');
    }
  };

  const handleSelectOffering = (selected: BitwerxOffering) => {
    searchParams.set('offeringId', selected.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('offeringId');
    history.push({ search: searchParams.toString() });
  };

  const [isManagePluginsModalOpen, setIsManagePluginsModalOpen] = useState(false);
  const [isCreateOfferingOpen, setIsCreateOfferingOpen] = useState(false);
  const [isAddSubOfferingOpen, setIsAddSubOfferingOpen] = useState(false);
  const [addEntityName, setAddEntityName] = useState('');
  const [addDisplayName, setAddDisplayName] = useState('');
  const [pendingDeletePlugin, setPendingDeletePlugin] = useState<OfferingPlugin | null>(null);
  const [pendingDeleteSubOffering, setPendingDeleteSubOffering] =
    useState<BitwerxSubOfferingDetail | null>(null);

  const handleAddSubOffering = () => {
    setAddEntityName('');
    setAddDisplayName('');
    setIsAddSubOfferingOpen(true);
  };

  const handleSubmitAddSubOffering = async () => {
    if (!offering || !addEntityName.trim()) return;
    const payload: UpdateBitwerxOfferingPayload = {
      id: offering.id,
      displayId: 0,
      name: '',
      subOfferings: [
        ...(offering.subOfferings ?? []).map(sub => ({
          name: sub.name,
          displayName: sub.displayName ?? sub.name,
          addition: false,
        })),
        {
          name: addEntityName.trim(),
          displayName: addDisplayName.trim() || addEntityName.trim(),
          addition: true,
        },
      ],
      createdById: offering.createdById,
      createdDate: offering.createdDate,
      createdByFirstName: offering.createdByFirstName,
      createdByLastName: offering.createdByLastName,
      modifiedById: offering.modifiedById,
      modifiedDate: offering.modifiedDate,
    };
    const result = await updateBitwerxOffering(offering.id, payload);
    if (result !== null) {
      toast.success('Sub offering added');
      setIsAddSubOfferingOpen(false);
      setAddEntityName('');
      setAddDisplayName('');
      refetchDetails();
      refetchOfferings();
    } else {
      toast.error('Failed to add sub offering');
    }
  };

  const handleDeleteSubOffering = (subOffering: BitwerxSubOfferingDetail) => {
    setPendingDeleteSubOffering(subOffering);
  };

  const handleConfirmDeleteSubOffering = async () => {
    if (!pendingDeleteSubOffering) return;
    const result = await removeBitwerxSubOffering(pendingDeleteSubOffering.id);
    setPendingDeleteSubOffering(null);
    if (result !== null) {
      toast.success('Sub offering removed');
      refetchDetails();
      refetchOfferings();
    } else {
      toast.error('Failed to remove sub offering');
    }
  };

  const handleCreateOffering = () => {
    setIsCreateOfferingOpen(true);
  };

  const handleAddPlugin = () => {
    setIsManagePluginsModalOpen(true);
  };

  const handleDeletePlugin = (plugin: OfferingPlugin) => {
    setPendingDeletePlugin(plugin);
  };

  const handleConfirmDeletePlugin = async () => {
    if (!pendingDeletePlugin || !offering?.name?.trim()) return;
    const response = await removePluginFromOffering(offering.name.trim(), pendingDeletePlugin.id);
    setPendingDeletePlugin(null);
    if (response !== null && response.isCompletedSuccessfully) {
      toast.success('Plugin removed from offering');
      await refetchPlugins();
    } else {
      toast.error('Failed to remove plugin from offering');
    }
  };

  const onCompletedDeleteOffering = () => {
    refetchOfferings();
    setIsCreateOfferingOpen(false);
    searchParams.delete('offeringId');
    history.push({ search: searchParams.toString() });
  };

  const onCompletedCreateOffering = (offeringId: string) => {
    refetchOfferings();
    refetchDetails();
    searchParams.set('offeringId', offeringId);
    history.push({ search: searchParams.toString() });
    setIsCreateOfferingOpen(false);
  };

  return (
    <SettingsPageLayout
      title="Bitwerx Offerings"
      actions={
        canModifyOfferings ? (
          <Button
            variant="primary"
            onClick={handleCreateOffering}
            icon={<FontAwesomeIcon icon={faPlus} />}>
            Create offering
          </Button>
        ) : undefined
      }>
      <ListDetailView
        selectedId={selectedOfferingId}
        list={
          <BitwerxOfferingsTable
            offerings={filteredData}
            loading={offeringsLoading}
            error={offeringsError}
            onSelectOffering={handleSelectOffering}
            selectedOfferingId={selectedOfferingId || undefined}
            sortConfig={sortConfig}
            onSortChange={handleSort}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search by name..."
          />
        }
        detail={
          <DetailPanel
            title={offering?.name || 'Offerings Details'}
            onClose={handleClosePanel}
            actions={
              canModifyOfferings && offering
                ? ([
                    {
                      key: 'edit',
                      label: 'Edit name',
                      icon: <FontAwesomeIcon icon={faPen} />,
                      onClick: handleStartEditName,
                    },
                    {
                      key: 'delete',
                      label: 'Delete offering',
                      icon: <FontAwesomeIcon icon={faTrash} />,
                      variant: 'danger' as const,
                      onClick: () => setIsConfirmDeleteOpen(true),
                    },
                  ] as ActionMenuItem[])
                : undefined
            }
            loading={detailsLoading}
            loadingMessage="Loading offerings details..."
            error={detailsError}
            errorMessage="There was an error fetching the offerings details."
            onRetry={refetchDetails}>
            {offering && (
              <div className={styles.detailContent}>
                <CollapsiblePanel title="Offering Information" defaultExpanded={true}>
                  <div className={detailFieldStyles.fieldContainer}>
                    <DetailField label="Name">{offering.name}</DetailField>
                    <DetailField label="Display ID">{offering.displayId}</DetailField>
                    <DetailField label="Created By">
                      {offering.createdByFirstName && offering.createdByLastName
                        ? `${offering.createdByFirstName} ${offering.createdByLastName}`
                        : '-'}
                    </DetailField>
                    <DetailField label="Created Date">
                      {offering.createdDate
                        ? moment.utc(offering.createdDate).local().format('M/D/YYYY LT')
                        : '-'}
                    </DetailField>
                    <DetailField label="Sub-offerings count">
                      {(offering.subOfferings ?? []).length}
                    </DetailField>
                  </div>
                </CollapsiblePanel>
                {canSeePluginManagement && (
                  <BitwerxOfferingPluginManagementPanel
                    offering={offering}
                    plugins={plugins}
                    pluginsLoading={pluginsLoading}
                    pluginsError={pluginsError}
                    onRetryPlugins={() => refetchPlugins()}
                    onAddPlugin={canAddOrRemovePlugin ? handleAddPlugin : undefined}
                    onDeletePlugin={canAddOrRemovePlugin ? handleDeletePlugin : undefined}
                  />
                )}
                <BitwerxOfferingSubOfferingPanel
                  offering={offering}
                  onAddSubOffering={canModifyOfferings ? handleAddSubOffering : undefined}
                  onDeleteSubOffering={canModifyOfferings ? handleDeleteSubOffering : undefined}
                  canModify={canModifyOfferings}
                />
              </div>
            )}
          </DetailPanel>
        }
      />
      {isManagePluginsModalOpen && (
        <ManageOfferingPlugins
          offeringName={offering?.name ?? null}
          onClose={() => setIsManagePluginsModalOpen(false)}
          onCompleted={() => {
            setIsManagePluginsModalOpen(false);
            refetchPlugins();
          }}
        />
      )}
      {isCreateOfferingOpen && (
        <ManageOfferingsForm
          offering={null}
          onClose={() => setIsCreateOfferingOpen(false)}
          onCompleted={() => {
            refetchDetails();
            refetchOfferings();
          }}
          onCompletedDeleteOffering={onCompletedDeleteOffering}
          onCompletedCreateOffering={onCompletedCreateOffering}
        />
      )}
      {isAddSubOfferingOpen && offering && (
        <Modal
          isOpen={true}
          onClose={() => setIsAddSubOfferingOpen(false)}
          title="Add Sub Offering"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setIsAddSubOfferingOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSubmitAddSubOffering}
                disabled={!addEntityName.trim() || updateOfferingLoading}
                isLoading={updateOfferingLoading}>
                Add
              </Button>
            </>
          }>
          <div className={styles.modalFormContent}>
            <Input
              label="Entity Name"
              value={addEntityName}
              onChange={e => setAddEntityName(e.target.value)}
              placeholder="Entity name"
              autoComplete="off"
            />
            <Input
              label="Display Name"
              value={addDisplayName}
              onChange={e => setAddDisplayName(e.target.value)}
              placeholder="Display name"
              autoComplete="off"
            />
          </div>
        </Modal>
      )}
      {isEditNameOpen && offering && (
        <Modal
          isOpen={true}
          onClose={() => setIsEditNameOpen(false)}
          title="Edit Offering Name"
          size="sm"
          footer={
            <>
              <Button variant="ghost" onClick={() => setIsEditNameOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEditName}
                disabled={
                  !editName.trim() || editName.trim() === offering.name || updateOfferingLoading
                }
                isLoading={updateOfferingLoading}>
                Save
              </Button>
            </>
          }>
          <Input
            label="Offering Name"
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSaveEditName();
            }}
            placeholder="Offering name"
            autoComplete="off"
          />
        </Modal>
      )}
      <DeleteConfirmationModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => setIsConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDeleteOffering}
        isDeleting={deleteOfferingLoading}
        title="Delete Bitwerx Offering"
        message={`Are you sure you want to delete ${offering?.name ?? 'this offering'}?`}
      />
      <DeleteConfirmationModal
        isOpen={pendingDeletePlugin !== null}
        onClose={() => setPendingDeletePlugin(null)}
        onConfirm={handleConfirmDeletePlugin}
        isDeleting={false}
        title="Remove Plugin"
        message={`Are you sure you want to remove ${pendingDeletePlugin?.name ?? 'this plugin'} from the offering?`}
      />
      <DeleteConfirmationModal
        isOpen={pendingDeleteSubOffering !== null}
        onClose={() => setPendingDeleteSubOffering(null)}
        onConfirm={handleConfirmDeleteSubOffering}
        isDeleting={false}
        title="Remove Sub Offering"
        message={`Are you sure you want to remove ${pendingDeleteSubOffering?.displayName ?? pendingDeleteSubOffering?.name ?? 'this sub offering'}?`}
      />
    </SettingsPageLayout>
  );
};

export default BitwerxOfferingsPage;
